import React, { useEffect, useRef } from 'react';
import './../styles/Intro.css';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { useTheme } from '../Navbar/Theme';
import profimg1 from '../../assets/prof2.jpeg';
import Skills from './Skills';
import Works from './Works';
import Contact from './Contact';
import About from './About';

// 1. Import the sound controller
import { sounds } from '../Utils/soundEffects';

// 2. Import your custom MP3 files
import tagHoverSfx from '../../assets/sounds/tag-hover.mp3';
import cardHoverSfx from '../../assets/sounds/whooshss.mp3';
import moodSwitchSfx from '../../assets/sounds/mood-switch.mp3';

gsap.registerPlugin(ScrollTrigger);

const hexToRgb = (hex) => {
  const clean = hex.trim().replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return { r: r || 0, g: g || 0, b: b || 0 };
};

const ZONE_POSITIONS = [
  { x: 15, y: 10 },
  { x: 18, y: 90 },
  { x: 88, y: 15 },
];

const Intro = () => {
  const introRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const haloContainerRef = useRef(null);
  const homeRef = useRef(null);

  // Consume active theme from context
  const { activeTheme, setActiveTheme, currentTheme, THEME_PRESETS } = useTheme();

  // Keep a ref synced to activeTheme so the pointer event listener reads fresh values
  const activeThemeRef = useRef(activeTheme);
  useEffect(() => {
    activeThemeRef.current = activeTheme;
  }, [activeTheme]);

  // 3. Preload all 3 MP3 files into memory on mount
  useEffect(() => {
    sounds.loadSound('tagHover', tagHoverSfx);
    sounds.loadSound('cardHover', cardHoverSfx);
    sounds.loadSound('moodSwitch', moodSwitchSfx);

    // Audio unlock listener for browser autoplay rules
    const handleFirstInteraction = () => {
      sounds.init();
      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };

    window.addEventListener('pointerdown', handleFirstInteraction);
    window.addEventListener('keydown', handleFirstInteraction);

    return () => {
      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
  }, []);

  // Cursor-tracking glow calculation
  useEffect(() => {
    const container = haloContainerRef.current;
    if (!container) return;

    let idleTimeout;

    const activate = () => {
      container.classList.add('cursor-active');
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => {
        container.classList.remove('cursor-active');
      }, 900);
    };

    const deactivate = () => {
      clearTimeout(idleTimeout);
      container.classList.remove('cursor-active');
    };

    const handleMove = (e) => {
      gsap.to(container, {
        '--mx': `${e.clientX}px`,
        '--my': `${e.clientY}px`,
        duration: 0.35,
        ease: 'power2.out',
        overwrite: 'auto',
      });
      activate();

      const currentGlowPreset = THEME_PRESETS[activeThemeRef.current].glows;
      const glowTints = [
        hexToRgb(currentGlowPreset.g1),
        hexToRgb(currentGlowPreset.g2),
        hexToRgb(currentGlowPreset.g3),
      ];

      const px = (e.clientX / window.innerWidth) * 100;
      const py = (e.clientY / window.innerHeight) * 100;

      const weights = ZONE_POSITIONS.map((pos) => {
        const dx = px - pos.x;
        const dy = py - pos.y;
        return 1 / (dx * dx + dy * dy + 45);
      });
      const totalWeight = weights.reduce((sum, w) => sum + w, 0);

      const blended = glowTints.reduce(
        (acc, c, i) => {
          const w = weights[i] / totalWeight;
          return { r: acc.r + c.r * w, g: acc.g + c.g * w, b: acc.b + c.b * w };
        },
        { r: 0, g: 0, b: 0 }
      );

      const finalR = Math.round(blended.r);
      const finalG = Math.round(blended.g);
      const finalB = Math.round(blended.b);

      container.style.setProperty('--glow-color', `rgb(${finalR}, ${finalG}, ${finalB})`);
      container.style.setProperty('--glow-color-soft', `rgba(${finalR}, ${finalG}, ${finalB}, 0.28)`);
    };

    const handleWindowLeave = (e) => {
      if (!e.relatedTarget && !e.toElement) deactivate();
    };

    window.addEventListener('pointermove', handleMove);
    document.addEventListener('mouseout', handleWindowLeave);
    window.addEventListener('blur', deactivate);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      document.removeEventListener('mouseout', handleWindowLeave);
      window.removeEventListener('blur', deactivate);
      clearTimeout(idleTimeout);
    };
  }, [THEME_PRESETS]);

  // Initial load entrance
  useEffect(() => {
    const tl = gsap.timeline();

    tl.fromTo(
      '.gradient-wash',
      { opacity: 0, filter: 'blur(70px)' },
      { opacity: 1, filter: 'blur(60px)', duration: 1.2, ease: 'power2.out' }
    )
    .to('.intro-content', { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }, '-=1.0')
    .fromTo(
      introRef.current.querySelector('.polaroid-stack-container'),
      { scale: 0.85, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.8, ease: 'back.out(1.4)' },
      '-=0.5'
    )
    .fromTo(
      introRef.current.querySelectorAll('.text-overlay h1, .text-overlay h2, .tags-box, .intro-bio, .mood-pill'),
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', stagger: 0.08 },
      '-=0.3'
    );
  }, []);

  // 4. Play moodSwitch sound here
  const handleThemeChange = (newIndex) => {
    if (newIndex === activeTheme) return;

    sounds.play('moodSwitch', 0.4);

    gsap.timeline()
      .to('.gradient-wash', {
        opacity: 0.15,
        duration: 0.5,
        ease: 'power2.in',
        onComplete: () => {
          setActiveTheme(newIndex);
        },
      })
      .to('.gradient-wash', {
        opacity: 1,
        duration: 1.4,
        ease: 'power2.out',
      });
  };

  return (
    <div
      id="home"
      ref={homeRef}
      style={{
        '--color-1': currentTheme.colors.c1,
        '--color-2': currentTheme.colors.c2,
        '--color-3': currentTheme.colors.c3,
        '--glow-1': currentTheme.glows.g1,
        '--glow-2': currentTheme.glows.g2,
        '--glow-3': currentTheme.glows.g3,
      }}
    >
      <div className="intro-bg">
        <div className="halftone-container" ref={haloContainerRef}>
          <div className="gradient-wash" />
          <div className="cursor-glow" />
          <div className="halftone-dots-layer" />
        </div>

        <div className="intro-content" ref={introRef}>
          <div className="image-box">
            <div className="polaroid-stack-container">
              {/* CARD 2: Back Mesh Gradient */}
              <div 
                className="polaroid-card polaroid-back"
                onMouseEnter={() => sounds.play('cardHover', 0.25)}
              >
                <div className="polaroid-arrow-btn">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="19" x2="19" y2="5" />
                    <polyline points="7 5 19 5 19 17" />
                  </svg>
                </div>
                <div className="polaroid-media-frame">
                  <div className="polaroid-mesh-gradient" />
                </div>
                <div className="polaroid-caption">
                  <p className="polaroid-caption-text">Currently overthinking</p>
                </div>
              </div>

              {/* CARD 1: Front Photo */}
              <div 
                className="polaroid-card polaroid-front"
                onMouseEnter={() => sounds.play('cardHover', 0.25)}
              >
                <div className="polaroid-arrow-btn">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="19" x2="19" y2="5" />
                    <polyline points="7 5 19 5 19 17" />
                  </svg>
                </div>
                <div className="polaroid-media-frame">
                  <img src={profimg1} alt="Navya" className="polaroid-img" />
                </div>
                <div className="polaroid-caption">
                  <p className="polaroid-caption-text">
                    Usually found somewhere between design and chaos.
                  </p>
                </div>
              </div>
            </div>

            {/* Mood Accent Theme Pill */}
            <div className="mood-pill">
              <div className="mood-dots-group">
                {THEME_PRESETS.map((preset, idx) => (
                  <button
                    key={preset.name}
                    type="button"
                    title={preset.name}
                    className={`mood-dot mood-dot-${idx} ${activeTheme === idx ? 'selected' : ''}`}
                    onClick={() => handleThemeChange(idx)}
                  />
                ))}
              </div>
              <span className="mood-label">Mood</span>
            </div>
          </div>

          <div className="intro1">
            <div className="text-overlay">
              <h1 ref={titleRef}>Hey there,</h1>
              <h2 ref={subtitleRef}>
                I'm <span className="navya-font">Navya</span>
              </h2>
            </div>
            <div className="wrapper">
              <div className="tags">
                <div 
                  className="tags-box"
                  onMouseEnter={() => sounds.play('tagHover', 0.2)}
                >
                  UI/UX Design
                </div>
                <div 
                  className="tags-box"
                  onMouseEnter={() => sounds.play('tagHover', 0.2)}
                >
                  Motion Graphics
                </div>
                <div 
                  className="tags-box"
                  onMouseEnter={() => sounds.play('tagHover', 0.2)}
                >
                  Web Developer
                </div>
              </div>

              <div className="typing-demo">
                <p className="intro-bio">
                  I love designing digital experiences that people enjoy using, spanning front-end
                  web development, UI/UX, and graphic design. I continuously explore these elements
                  in my work to create visually compelling interfaces.
                </p>
              </div>
            </div>
          </div>
        </div>
        <About />
        <Skills />
        <Works />
        <Contact />
      </div>
    </div>
  );
};

export default Intro;